import type { Service } from "@abejon/shared";

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
}

interface ServiceCardProps {
  service: Service;
  onEdit?: (service: Service) => void;
  onDelete?: (service: Service) => void;
  showActions?: boolean;
}

export function ServiceCard({ service, onEdit, onDelete, showActions = false }: ServiceCardProps) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-gray-900">{service.name}</h3>
        {service.description && (
          <p className="mt-0.5 text-sm text-gray-500 line-clamp-1">{service.description}</p>
        )}
        <div className="mt-1 flex items-center gap-3 text-sm text-gray-600">
          <span>{formatDuration(service.durationMinutes)}</span>
          <span className="font-medium text-gray-900">{formatPrice(service.priceInCents)}</span>
        </div>
      </div>

      {showActions && (
        <div className="ml-4 flex shrink-0 gap-2">
          {onEdit && (
            <button
              onClick={() => onEdit(service)}
              aria-label={`Edit ${service.name}`}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(service)}
              aria-label={`Delete ${service.name}`}
              className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
